import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUp, Linkedin, Twitter, MapPin, Mail, Phone } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const Footer = () => {
  const { t } = useLanguage();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const footerLinks = [
    {
      title: 'Navegación',
      links: [
        { label: 'Solución', href: '#solution' },
        { label: 'Equipo', href: '#crew' },
        { label: 'Contacto', href: '#contact' },
      ],
    },
    {
      title: 'Grupo ALLCOM',
      links: [
        { label: 'ALLCOM Chile', href: 'https://www.allcom.cl', external: true },
        { label: 'ALLCOM Tech', href: 'https://www.allcomtech.cl', external: true },
      ],
    },
  ];

  return (
    <footer
      data-testid="footer"
      className="bg-slate-950 border-t border-slate-800"
    >
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="/images/allcom-logo.png" 
                alt="ALLCOM" 
                className="h-10 w-auto brightness-0 invert"
              />
              <div>
                <span className="text-white font-semibold text-lg tracking-tight">ALLCOM</span>
                <span className="text-slate-500 text-xs block -mt-1 tracking-wider">IA TECHNOLOGIES</span>
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-6 max-w-sm" data-cms="footer.tagline">
              {t('footer.tagline')}
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3 text-sm text-slate-400">
                <MapPin className="w-4 h-4 text-slate-500" />
                <span>Andrés Bello 1567, Providencia, Santiago, Chile</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-400">
                <Mail className="w-4 h-4 text-slate-500" />
                <a href="mailto:ventas@allcomtech.cl" className="hover:text-white transition-colors">
                  ventas@allcomtech.cl
                </a>
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-400">
                <Phone className="w-4 h-4 text-slate-500" />
                <span>+56 2 2345 6789</span>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <motion.a
                href="#"
                data-testid="social-linkedin"
                className="w-10 h-10 bg-slate-800 flex items-center justify-center hover:bg-slate-700 transition-colors"
                whileHover={{ scale: 1.05 }}
              >
                <Linkedin className="w-4 h-4" />
              </motion.a>
              <motion.a
                href="#"
                data-testid="social-twitter"
                className="w-10 h-10 bg-slate-800 flex items-center justify-center hover:bg-slate-700 transition-colors"
                whileHover={{ scale: 1.05 }}
              >
                <Twitter className="w-4 h-4" />
              </motion.a>
            </div>
          </div>

          {/* Links Columns */}
          {footerLinks.map((column, index) => (
            <div key={index}>
              <h4 className="text-xs font-medium tracking-wider uppercase text-slate-500 mb-4">
                {column.title}
              </h4>
              <ul className="space-y-3">
                {column.links.map((link, i) => (
                  <li key={i}>
                    <a
                      href={link.href}
                      target={link.external ? '_blank' : undefined}
                      rel={link.external ? 'noopener noreferrer' : undefined}
                      className="text-sm text-slate-400 hover:text-white transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-6 text-sm text-slate-500">
              <span data-cms="footer.rights">{t('footer.rights')}</span>
              <span>•</span>
              <span data-cms="footer.location">{t('footer.location')}</span>
            </div>
            <motion.button
              data-testid="scroll-to-top"
              onClick={scrollToTop}
              className="w-10 h-10 bg-slate-800 flex items-center justify-center hover:bg-slate-700 transition-colors"
              whileHover={{ scale: 1.05 }}
            >
              <ArrowUp className="w-4 h-4" />
            </motion.button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
